"use client";
import { Vehicle, VehicleStatusType } from "@alba-cars/common-modules";
import { useTranslations, useLocale } from "next-intl";
import Image from "next/image";
import { useParams, useSearchParams } from "next/navigation";
import React from "react";

import PercentageSlider from "@/components/common/PercentageSlider";
import SectionTitle from "@/components/ui/SectionTitle";
import { cn, getCarMonthlyInstallment, getCarPrice } from "@/lib/utils";
import LoanSummaryCard from "./LoanSummaryCard";
import LoanTenureSelector from "./LoanTenureSelector";
import { useRouter } from "@/i18n/routing";


function resolveEMIPrice(car: Vehicle): { price: number; installment: number } {
  const isTimedSaleActive = car.finance?.isTimedSaleActive === true && Number(car.finance?.salePrice) > 0;

  if (isTimedSaleActive) {
    return {
      price: Number(car.finance!.salePrice),
      installment:
        Number(car.finance?.saleMonthlyInstallment) > 0
          ? Number(car.finance!.saleMonthlyInstallment)
          : getCarMonthlyInstallment(car),
    };
  }

  const hasDroppedPrice = car.finance?.vehiclePriceType === "dropped_price" && Number(car.finance?.droppedPrice) > 0;

  if (hasDroppedPrice) {
    return {
      price: Number(car.finance!.droppedPrice),
      installment:
        Number(car.finance?.droppedMonthlyInstallment) > 0
          ? Number(car.finance!.droppedMonthlyInstallment)
          : getCarMonthlyInstallment(car),
    };
  }

  return {
    price: Number(car.finance?.price) || getCarPrice(car),
    installment: getCarMonthlyInstallment(car),
  };
}

const CarDetailsEMI = ({ car, className }: { car: Vehicle; className?: string }) => {
  const t = useTranslations("CarDetails.emi");
  const locale = useLocale();
  const isRTL = locale === "ar";
  const [EMI, setEMI] = React.useState<{
    actualValue: string;
    percentage: number;
  }>({ actualValue: "0", percentage: 0 });

  const { slug } = useParams();
  const searchParams = useSearchParams();

  const router = useRouter();

  const [loanTenure, setLoanTenure] = React.useState<number>(5);

  const { price: carPrice, installment } = resolveEMIPrice(car);

  return (
    <>
      {!!installment && (
        <div className={cn("rounded-3xl bg-white p-8", className)}>
          <div className={`flex flex-col lg:flex-row ${!isRTL ? "lg:space-x-5" : ""}`}>
            <div className="lg:w-1/2 xl:mb-3">
              <SectionTitle
                title={t("title")}
                showButton={false}
                showIcon={false}
                className="!mb-2 lg:!mb-4 xl:!mb-8"
              />
            </div>
            <div className="mb-3 rounded-2xl border border-primary-light bg-primary-light lg:w-1/2">
              <div className={`flex items-center gap-4 pb-2 pt-2 ${isRTL ? "pr-3 lg:pr-8" : "pl-3 lg:pl-8"}`}>
                <Image alt="" src={"/assets/images/percentage.png"} width={65} height={65}></Image>
                <div>
                  <p className="font-inter text-base font-bold text-primary">{t("downpayment")}</p>
                  <p className="font-inter text-base font-bold text-black">{t("freeWarranty")}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-y-3 lg:grid-cols-2 lg:gap-x-4">
            <div className="rounded-3xl border border-secondary-light p-4 font-inter text-sm font-medium text-secondary-foreground">
              <h4>{t("selectDownpayment")}</h4>
              <PercentageSlider totalValue={carPrice} initialPercentage={0} onChange={setEMI} />
              <LoanTenureSelector carYear={car.year} onChange={setLoanTenure} selectedYear={loanTenure} />
            </div>
            <LoanSummaryCard
              tenure={loanTenure}
              totalPrice={carPrice}
              downPaymentPercentage={EMI.percentage ?? 0}
              interestRate={car?.finance?.interestPercentage ?? 3.5}
              car={car}
              onStartFinancing={() => {
                router.push(`/used-car-finance`);
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default CarDetailsEMI;
